import { styled } from '@mui/system';
import { alpha } from '@mui/material/styles';
import { Button, Card } from '@mui/material';
import { MOBILE_BREAK_POINT, TABLET_BREAK_POINT } from './constants';

// Main container
export const Main = styled('div')(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	position: 'relative',
	isolation: 'isolate',
	overflow: 'hidden',
	backgroundColor: theme.palette.atlas.background.app,
	minHeight: '100vh',
	width: '100%',
	'&::before, &::after': {
		content: '""',
		position: 'absolute',
		borderRadius: '9999px',
		pointerEvents: 'none',
		zIndex: 0,
	},
	'&::before': {
		width: '42vw',
		height: '42vw',
		minWidth: 280,
		minHeight: 280,
		left: '-10vw',
		top: '8vh',
		background:
			theme.palette.mode === 'dark'
				? 'radial-gradient(circle, rgba(22, 163, 74, 0.2) 0%, rgba(22, 163, 74, 0) 72%)'
				: 'radial-gradient(circle, rgba(251, 146, 60, 0.2) 0%, rgba(251, 146, 60, 0) 72%)',
	},
	'&::after': {
		width: '40vw',
		height: '40vw',
		minWidth: 260,
		minHeight: 260,
		right: '-12vw',
		bottom: '4vh',
		background:
			theme.palette.mode === 'dark'
				? 'radial-gradient(circle, rgba(30, 64, 175, 0.2) 0%, rgba(30, 64, 175, 0) 72%)'
				: 'radial-gradient(circle, rgba(134, 239, 172, 0.2) 0%, rgba(134, 239, 172, 0) 72%)',
	},
	'& > *': {
		position: 'relative',
		zIndex: 1,
	},
	paddingTop: 84,
	[`@media (max-width: ${MOBILE_BREAK_POINT}px)`]: {
		paddingTop: 0,
		overflow: 'hidden',
	},
}));

export const AgentWrapper = styled('div')(({ theme }) => ({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'center',
	padding: '40px 0',
	width: '100%',
	backgroundColor:
		theme.palette.mode === 'light'
			? 'rgba(255, 255, 255, 0.5)'
			: 'transparent',
	[`@media (max-width: ${TABLET_BREAK_POINT}px)`]: {
		padding: '24px 0',
	},
	[`@media (max-width: ${MOBILE_BREAK_POINT}px)`]: {
		padding: '16px 12px',
	},
}));

// Card styles
export const StyledCard = styled(Card)(({ theme }) => ({
	background: theme.palette.atlas.background.card,
	borderRadius: 0,
	clipPath:
		'polygon(15px 0, 100% 0, 100% calc(100% - 15px), calc(100% - 15px) 100%, 0 100%, 0 15px)',
	padding: '1px',
	boxSizing: 'border-box',
	border: 'none',
	boxShadow: 'none',
}));

export const StyledCardReverse = styled(Card)(({ theme }) => ({
	backgroundColor:
		theme.palette.mode === 'light'
			? 'rgba(255, 255, 255, 0.5)'
			: 'rgba(7, 8, 17, 0.5)',
	borderRadius: 0,
	clipPath:
		'polygon(0 0, calc(100% - 15px) 0, 100% 15px, 100% 100%, 15px 100%, 0 calc(100% - 15px))',
	boxSizing: 'border-box',
	padding: '1px',
	border: 'none',
	boxShadow: 'none',
	height: '100%',
}));

export const StyledCardInner = styled('div')(({ theme }) => ({
	width: '100%',
	height: '100%',
	backgroundColor:
		theme.palette.mode === 'light'
			? 'rgba(255, 255, 255, 0.5)'
			: 'rgba(7, 8, 17, 0.5)',
	//backgroundColor: theme.palette.atlas.background.surface,
	//backdropFilter: 'blur(24px) saturate(40%)',
	//WebkitBackdropFilter: 'blur(24px) saturate(40%)',
	clipPath:
		'polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px)',
	display: 'flex',
	flexDirection: 'column',
	overflow: 'visible',
	'& .MuiCardContent-root': {
		overflow: 'visible',
	},
}));

export const StatsCardInnerReverse = styled('div')(({ theme }) => ({
	width: '100%',
	height: '100%',
	backgroundColor:
		theme.palette.mode === 'light'
			? 'rgba(255, 255, 255, 0.5)'
			: 'rgba(7, 8, 17, 0.5)',
	//backgroundColor: theme.palette.atlas.background.surface,
	//backdropFilter: 'blur(24px) saturate(40%)',
	//WebkitBackdropFilter: 'blur(24px) saturate(40%)',
	clipPath:
		'polygon(0 0,calc(100% - 14px) 0,100% 14px,100% 100%,14px 100%,0 calc(100% - 14px))',
	display: 'flex',
	flexDirection: 'column',
	overflow: 'visible',
	'& .MuiCardContent-root': {
		overflow: 'visible',
	},
}));

export const StatsCard = styled(StyledCard)(({ theme }) => ({
	height: '100%',
	overflow: 'visible',
	backgroundColor:
		theme.palette.mode === 'light'
			? 'rgba(255, 255, 255, 0.5)'
			: 'rgba(7, 8, 17, 0.5)',
	backdropFilter: 'blur(24px) saturate(40%)',
	WebkitBackdropFilter: 'blur(24px) saturate(40%)',
}));

// Button styles
export const RiskButton = styled(Button)<{ selected: boolean }>(
	({ theme, selected }) => ({
		padding: '12px',
		marginBlock: '6px',
		border: `1px solid ${
			selected
				? theme.palette.atlas.border.muted
				: theme.palette.atlas.border.subtle
		}`,
		backgroundColor: selected
			? theme.palette.atlas.background.surfaceSelected
			: theme.palette.atlas.border.muted,
		color: selected
			? theme.palette.atlas.accent.primaryAlt
			: theme.palette.primary.main,
		fontFamily: 'Oxanium, sans-serif',
		textTransform: 'none',
		'&:hover': {
			backgroundColor: selected
				? theme.palette.atlas.accent.primaryGlowAlpha20
				: alpha(theme.palette.atlas.border.neutral, 0.1),
		},
		'&:disabled': {
			backgroundColor: selected
				? theme.palette.atlas.accent.primaryGlowAlpha20
				: alpha(theme.palette.atlas.border.neutral, 0.1),
			opacity: 0.5,
		},
	}),
);

export const LaunchButton = styled(Button)(({ theme }) => ({
	backgroundColor: theme.palette.primary.main,
	width: 'min-content',
	whiteSpace: 'nowrap',
	color: '#fff',
	padding: '12px 70px',
	fontFamily: 'AmigaForever',
	fontWeight: 600,
	fontSize: '14px',
	letterSpacing: '0.15em',
	textTransform: 'uppercase',
	boxShadow: 'none',
	clipPath:
		'polygon(15px 0, 100% 0, 100% calc(100% - 15px), calc(100% - 15px) 100%, 0 100%, 0 15px)',
	'&:hover': {
		backgroundColor: theme.palette.primary.dark,
		boxShadow: 'none',
	},
	'&:disabled': {
		backgroundColor: theme.palette.atlas.background.surfaceMuted,
		boxShadow: 'none',
		color: theme.palette.text.disabled,
	},
}));

export const Corner = styled('div')(({ theme }) => ({
	position: 'absolute',
	width: 20,
	height: 20,
	backgroundColor: theme.palette.atlas.border.default,
	clipPath: 'polygon(0 0, 100% 0, 100% 4px, 4px 4px, 4px 100%, 0 100%)',
}));

export const AgentInfo = styled('div')(({ theme }) => ({
	display: 'flex',
	backgroundColor: 'transparent',
	backdropFilter: 'blur(50px) saturate(40%)',
	WebkitBackdropFilter: 'blur(50px) saturate(40%)',
	padding: 32,
	borderBottom: `1px solid ${theme.palette.atlas.border.default}`,

	[`@media (max-width: ${MOBILE_BREAK_POINT}px)`]: {
		padding: '16px 12px',
	},
}));

export const AgentInfoChild = styled('div')`
	position: relative;
	width: 100%;
	height: 100%;
`;

export const FrameGrid = styled('div')`
	display: grid;
	grid-template-columns: 1fr 1fr;

	@media (max-width: ${MOBILE_BREAK_POINT}px) {
		display: none;
	}
`;

export const Item = styled('div')`
	display: flex;
	flex-direction: column;
	padding: 24px;

	@media (max-width: ${MOBILE_BREAK_POINT}px) {
		padding: 8px;
	}
`;

export const FooterWrapper = styled('div')`
	display: grid;
	grid-area: footer;
`;
